/* eslint-disable @stencil-community/own-props-must-be-private */
import {
  Component,
  Host,
  h,
  Prop,
  Element,
  State,
  Method,
  getAssetPath,
  JSX
} from "@stencil/core";

import { Locale } from "../../common/locale";

import { config } from "../../common/config";
import { EntityData } from "../../common/types";
import { MercuryBundles, getIconPath } from "@genexus/mercury";
import { TabularGridSelectionChangedEvent } from "@genexus/chameleon-controls-library";

// Best performance bundle
const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/button",
  "components/edit",
  "components/tabular-grid",
  "components/icon",
  "utils/form",
  "utils/layout",
  "utils/spacing",
  "chameleon/scrollbar"
];

const FILTER_ICON = getIconPath({
  category: "window-tools",
  name: "filter",
  colorType: "on-surface"
});

@Component({
  tag: "gx-ide-data-selector",
  styleUrl: "data-selector.scss",
  shadow: { delegatesFocus: true },
  assetsDirs: ["gx-ide-assets/data-selector"]
})
export class GxIdeDataSelector {
  #firstGridObjectId: string; // used to select the first item after "loadItemsCallback"
  #loadItemsCallbackJustCalled: boolean = false; // used to select the first item after "loadItemsCallback"

  #componentLocale: any;
  #animateEmptyState: boolean = false;
  #shortcutsSrc = getAssetPath(`./gx-ide-assets/data-selector/shortcuts.json`);
  /* References needed to collect data */
  #filterPatternEl!: HTMLChEditElement;
  #filterObjectEl!: HTMLGxIdeEntitySelectorElement;
  #chShortcutsEl: HTMLChShortcutsElement;
  #chTabularGridObjectsEl: HTMLChTabularGridElement;

  @Element() el: HTMLGxIdeDataSelectorElement;

  /**
   * True if loadItemsCallback has been called and has not been resolved yet.
   */
  @State() loading: boolean = true;

  /**
   * The objects rendered in the table
   */
  @State() objects: ItemData[] = [];

  /**
   * The selected objects in the table of objects
   */
  @State() selectedObjectsIds: string[] = [];

  /**
   * Callback invoked when the user wishes to cancel the selection of objects.
   */
  @Prop() readonly cancelCallback!: CancelCallback;

  /**
   * Callback invoked when the user presses the 'OK' button
   */
  @Prop() readonly confirmCallback!: ConfirmCallback;

  /**
   * The default value for the module/folder filter
   */
  @Prop() readonly defaultModule: EntityData;

  /**
   * If true it displays the component title on the header
   */
  @Prop() readonly displayTitle = false;

  /**
   * Callback invoked when user presses the edit button. Receives the first
   * selected element id as a parameter.
   */
  @Prop() readonly editCallback: EditCallback;

  /**
   * Callback invoked when the component needs to reload the list of attributes.
   */
  @Prop() readonly loadItemsCallback!: LoadItemsCallback;

  /**
   * If true, it will display a loader when needed.
   */
  @Prop() readonly loader = false;

  /**
   * Callback invoked when the user presses the 'New' button.
   */
  @Prop() readonly newVariableCallback: NewVariableCallback;

  /**
   * Callback invoked when the action is executed in the Object filter. It returns
   * the information of the selected object (id and name) or 'undefined' if it was
   * canceled.
   */
  @Prop() readonly selectObjectCallback: SelectObjectCallback;

  /**
   * Single if multiple object selection is not allowed. Default is multiple
   */
  @Prop() readonly selectionType: "single" | "multiple" = "multiple";

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
  }

  connectedCallback() {
    this.#refreshUIWithNewConfiguration();
  }

  componentDidLoad() {
    this.#filterPatternEl.focus();
  }

  componentDidRender() {
    this.#evaluateFirstRowSelection();
  }

  /**
   * Suspends or reactivates the shortcuts
   */
  @Method()
  async suspendShortcuts(suspendShortcuts: boolean) {
    if (suspendShortcuts) {
      this.#chShortcutsEl.suspend = true;
    } else {
      this.#chShortcutsEl.suspend = false;
    }
  }

  /**
   * Validate necessary data input
   */
  @Method()
  async validate(): Promise<boolean> {
    const isValid = true;
    return isValid;
  }

  /**
   * After items have been loaded and rendered, the first row should be selected.
   */
  #evaluateFirstRowSelection = () => {
    if (this.#loadItemsCallbackJustCalled) {
      this.#chTabularGridObjectsEl.selectRow(this.#firstGridObjectId);
    }
    this.#loadItemsCallbackJustCalled = false;
  };

  #chGridKeyDownHandler = (e: KeyboardEvent) => {
    if (e.key === "Enter") {
      this.#confirmCallbackHandler(e);
    }
  };

  #confirmCallbackHandler = (e: MouseEvent | KeyboardEvent): void => {
    e.stopPropagation();
    this.confirmCallback(this.selectedObjectsIds);
  };

  #editCallbackHandler = async (e: MouseEvent): Promise<void> => {
    e.stopPropagation();
    if (this.selectedObjectsIds[0]) {
      const result = await this.editCallback(this.selectedObjectsIds[0]);
      if (result) {
        this.#refreshUIWithNewConfiguration();
      }
    }
  };

  #hostKeyPressHandler = (e: KeyboardEvent) => {
    // TODO : revise why this is necessary
    // just prevent keypress propagation
    if (e.key === "Enter") {
      e.stopPropagation();
    }
  };

  #objectsSelectionChangedHandler = (
    event: CustomEvent<TabularGridSelectionChangedEvent>
  ) => {
    this.selectedObjectsIds = event.detail.rowsId;
  };

  #newVariableCallbackHandler = async () => {
    const response = await this.newVariableCallback();
    if (response) {
      this.#refreshUIWithNewConfiguration();
    }
  };
  #refreshUIWithNewConfiguration = (): void => {
    this.objects = [];
    const filters: FiltersData = {
      pattern: this.#filterPatternEl?.value,
      object: this.#filterObjectEl?.value?.id
    };
    this.loading = true;
    this.loadItemsCallback(filters).then((items: ItemData[]) => {
      if (items.length) {
        this.objects = this.#sortObjectsByName(items);
        this.#firstGridObjectId = this.objects[0].id;
        this.#loadItemsCallbackJustCalled = true;
      }
      this.selectedObjectsIds = [];
      this.loading = false;
    });
  };

  #renderFilter = (): Element[] => {
    return [
      <div class="header__field-group field-group">
        <div class="field field-block pattern-field">
          <label class="label" htmlFor="filter-pattern">
            {this.#componentLocale.filter.pattern}
          </label>
          <ch-edit
            autoFocus
            id="filter-pattern"
            name="filter-pattern"
            class="input pattern-input"
            part="filter-pattern"
            debounce={config.inputDebounce}
            onInput={this.#refreshUIWithNewConfiguration}
            ref={(el: HTMLChEditElement) =>
              (this.#filterPatternEl = el as HTMLChEditElement)
            }
          ></ch-edit>
        </div>
        <gx-ide-entity-selector
          id="object-selector"
          value={this.defaultModule}
          defaultValue={this.defaultModule}
          selectEntityCallback={this.selectObjectCallback}
          ref={(el: HTMLGxIdeEntitySelectorElement) =>
            (this.#filterObjectEl = el as HTMLGxIdeEntitySelectorElement)
          }
          class="entity"
          onValueChanged={this.#refreshUIWithNewConfiguration}
        ></gx-ide-entity-selector>
      </div>
    ];
  };

  #renderObjects = (): Element => {
    const gridIsEmpty = !this.objects.length;
    const displayLoader = this.loader && this.loading;
    if (!gridIsEmpty || displayLoader) {
      this.#animateEmptyState = true; // improve performance if is first render
    }
    return (
      <ch-tabular-grid
        class={{
          "tabular-grid": true,
          "empty-result": this.objects.length === 0,
          "data": true
        }}
        rowSelectionMode={this.selectionType}
        onKeyDown={this.#chGridKeyDownHandler}
        part="ch-grid-objects"
        onSelectionChanged={
          this.objects.length ? this.#objectsSelectionChangedHandler : undefined
        }
        ref={(el: HTMLChTabularGridElement) =>
          (this.#chTabularGridObjectsEl = el as HTMLChTabularGridElement)
        }
      >
        <ch-tabular-grid-columnset class="tabular-grid-column-set">
          <ch-tabular-grid-column
            column-name-position="text"
            settingable={false}
            sortable={false}
            size={config.tabularGrid.colSize.maxContent}
            class="tabular-grid-column"
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            column-name={this.#componentLocale.tableHead.name}
            column-name-position="text"
            settingable={false}
            sortable={false}
            size={config.tabularGrid.colSize.common}
            class="tabular-grid-column"
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            column-name={this.#componentLocale.tableHead.dataType}
            column-name-position="text"
            settingable={false}
            sortable={false}
            size={config.tabularGrid.colSize.common}
            class="tabular-grid-column"
          ></ch-tabular-grid-column>
          <ch-tabular-grid-column
            column-name={this.#componentLocale.tableHead.description}
            column-name-position="text"
            settingable={false}
            sortable={false}
            size={config.tabularGrid.colSize.common}
            class="tabular-grid-column"
          ></ch-tabular-grid-column>
        </ch-tabular-grid-columnset>

        {!gridIsEmpty ? (
          <ch-tabular-grid-rowset class="tabular-grid-rowset">
            {this.objects.map((obj: ItemData) => (
              <ch-tabular-grid-row
                rowid={obj.id}
                onDblClick={this.#confirmCallbackHandler}
                class="tabular-grid-row"
              >
                <ch-tabular-grid-cell class="tabular-grid-cell">
                  <ch-image
                    src={
                      obj.type === "attribute"
                        ? "objects/attribute"
                        : "objects-parts/variables"
                    }
                    class="icon-s"
                  ></ch-image>
                </ch-tabular-grid-cell>
                <ch-tabular-grid-cell class="tabular-grid-cell">
                  {obj.name}
                </ch-tabular-grid-cell>
                <ch-tabular-grid-cell class="tabular-grid-cell">
                  {obj.dataType}
                </ch-tabular-grid-cell>
                <ch-tabular-grid-cell class="tabular-grid-cell">
                  {obj.description}
                </ch-tabular-grid-cell>
              </ch-tabular-grid-row>
            ))}
          </ch-tabular-grid-rowset>
        ) : (
          <ch-tabular-grid-rowset class="tabular-grid-rowset">
            <ch-tabular-grid-rowset-empty>
              {displayLoader ? (
                <gx-ide-loader
                  loaderTitle={this.#componentLocale.loader.title}
                  show
                ></gx-ide-loader>
              ) : (
                <gx-ide-empty-state
                  isAnimated={this.#animateEmptyState}
                  stateIconSrc={FILTER_ICON}
                  stateTitle={this.#componentLocale.emptyState.title}
                ></gx-ide-empty-state>
              )}
            </ch-tabular-grid-rowset-empty>
          </ch-tabular-grid-rowset>
        )}
      </ch-tabular-grid>
    );
  };

  #renderEditButtons = (): JSX.Element | null => {
    if (!this.editCallback && !this.newVariableCallback) {
      return null;
    }

    const newVariableButtonEnabled = this.newVariableCallback && !this.loading;
    const editButtonEnabled =
      this.editCallback && !this.loading && this.selectedObjectsIds.length;

    return (
      <div class="buttons-spacer">
        {this.newVariableCallback ? (
          <button
            // New Variable Button
            class="button-secondary"
            disabled={!newVariableButtonEnabled}
            part="button button-new"
            type="button"
            onClick={this.#newVariableCallbackHandler}
          >
            {this.#componentLocale.footer.btnNew}
          </button>
        ) : (
          []
        )}
        {this.editCallback ? (
          <button
            // Edit Button
            class="button-secondary"
            disabled={!editButtonEnabled}
            part="button button-edit"
            type="button"
            onClick={editButtonEnabled && this.#editCallbackHandler}
          >
            {this.#componentLocale.footer.btnEdit}
          </button>
        ) : (
          []
        )}
      </div>
    );
  };

  #sortObjectsByName = (objects: ItemData[]): ItemData[] =>
    // TODO (make this a common helper function
    // and use it in other dialog grids that
    // require filter by name).
    objects.sort((a, b) => {
      const nameA = a.name.toLowerCase(),
        nameB = b.name.toLowerCase();
      if (nameA < nameB) {
        return -1;
      }
      if (nameA > nameB) {
        return 1;
      }
      return 0;
    });

  render() {
    const okButtonEnabled = !this.loading;
    const footerJustifySpaceBetween: boolean =
      !!this.newVariableCallback || !!this.editCallback;

    return (
      <Host onKeyPress={this.#hostKeyPressHandler} class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-shortcuts
          src={this.#shortcutsSrc}
          ref={(el: HTMLChShortcutsElement) =>
            (this.#chShortcutsEl = el as HTMLChShortcutsElement)
          }
        ></ch-shortcuts>

        <section class="section">
          <header class="header dialog-header-with-border spacing-body-block-start spacing-body-inline">
            {this.#renderFilter()}
          </header>
          {this.#renderObjects()}
          <footer
            class={{
              "footer": true,
              "dialog-footer-with-border": true,
              "dialog-footer-space-between": footerJustifySpaceBetween,
              "spacing-body-inline": true,
              "spacing-body-block-end": true
            }}
          >
            {this.#renderEditButtons()}
            <div class="buttons-spacer">
              <button
                // Cancel Button
                class="button-secondary"
                part="button button-cancel"
                type="button"
                onClick={this.cancelCallback}
              >
                {this.#componentLocale.footer.btnCancel}
              </button>
              <button
                // Confirm Button
                class="button-primary"
                part="button button-ok"
                disabled={!okButtonEnabled}
                type="button"
                onClick={okButtonEnabled && this.#confirmCallbackHandler}
              >
                {this.#componentLocale.footer.btnConfirm}
              </button>
            </div>
          </footer>
        </section>
      </Host>
    );
  }
}

export type NewVariableCallback = () => Promise<boolean> | undefined;

export type CancelCallback = () => Promise<void>;

export type SelectObjectCallback = () => Promise<EntityData | undefined>;

export type EditCallback = (id: string) => Promise<boolean> | undefined;

export type ConfirmCallback = (ids: string[]) => Promise<void>;

export type LoadItemsCallback = (filters: FiltersData) => Promise<ItemData[]>;

export type FiltersData = {
  pattern?: string;
  object?: string;
};

export type ItemData = {
  type: ItemType;
  id: string;
  name: string;
  dataType: string;
  description: string;
};

export type ItemType = "variable" | "attribute";
